import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import {
  LEARNING_PLAN,
  TOPIK_GENERATION,
  VOCAB_EXPANSION,
} from "../../../constants/ai-tutor";
import { Skeleton } from "primereact/skeleton";
import TopikInteractiveCard from "./topik/TopikInteractiveCard";

// Custom components for markdown rendering
const markdownComponents = {
  code({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    return !inline && match ? (
      <SyntaxHighlighter
        style={tomorrow}
        language={match[1]}
        PreTag="div"
        {...props}
      >
        {String(children).replace(/\n$/, "")}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  h1: ({ children }) => <h1 className="markdown-h1">{children}</h1>,
  h2: ({ children }) => <h2 className="markdown-h2">{children}</h2>,
  h3: ({ children }) => <h3 className="markdown-h3">{children}</h3>,
  h4: ({ children }) => <h4 className="markdown-h4">{children}</h4>,
  h5: ({ children }) => <h5 className="markdown-h5">{children}</h5>,
  h6: ({ children }) => <h6 className="markdown-h6">{children}</h6>,
  p: ({ children }) => <p className="markdown-p">{children}</p>,
  ul: ({ children }) => <ul className="markdown-ul">{children}</ul>,
  ol: ({ children }) => <ol className="markdown-ol">{children}</ol>,
  li: ({ children }) => <li className="markdown-li">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="markdown-blockquote">{children}</blockquote>
  ),
  strong: ({ children }) => (
    <strong className="markdown-strong">{children}</strong>
  ),
  em: ({ children }) => <em className="markdown-em">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="markdown-link"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  table: ({ children }) => <table className="markdown-table">{children}</table>,
  thead: ({ children }) => <thead className="markdown-thead">{children}</thead>,
  tbody: ({ children }) => <tbody className="markdown-tbody">{children}</tbody>,
  tr: ({ children }) => <tr className="markdown-tr">{children}</tr>,
  th: ({ children }) => <th className="markdown-th">{children}</th>,
  td: ({ children }) => <td className="markdown-td">{children}</td>,
};

// Template cho text thông thường
export const textTemplate = (message) => {
  const hasMarkdown = /[#*`\[\]()>|~]/.test(message.content);

  if (hasMarkdown) {
    return (
      <ReactMarkdown
        components={markdownComponents}
        remarkPlugins={[remarkGfm]}
        className="markdown-content"
      >
        {message.content}
      </ReactMarkdown>
    );
  }

  return (
    <div className={`message-text ${message.isStreaming ? "streaming" : ""}`}>
      {message.content}
    </div>
  );
};

// Move skeleton components outside to prevent recreation on each render
const HeaderSkeleton = React.memo(() => (
  <div className="plan-header">
    <div className="w-full">
      <Skeleton width="60%" height="2rem" className="mb-2" />
      <Skeleton width="90%" className="mb-2" />
      <Skeleton width="82%" />
    </div>
  </div>
));
HeaderSkeleton.displayName = "HeaderSkeleton";

const DailySkeleton = React.memo(() => (
  <div className="plan-section daily-plan-section">
    <Skeleton width="100%" height="2rem" className="my-2" />
    <div className="daily-plan-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="day-card">
          <div className="day-header mb-2">
            <Skeleton width="40%" />
            <Skeleton width="25%" />
          </div>
          <Skeleton width="80%" className="mb-2" />
          <Skeleton width="70%" className="mb-1" />
          <Skeleton width="55%" className="mb-1" />
        </div>
      ))}
    </div>
  </div>
));
DailySkeleton.displayName = "DailySkeleton";

const StrategiesSkeleton = React.memo(() => (
  <div className="plan-section strategies-section">
    <Skeleton width="100%" height="2rem" className="mb-2" />
    <div className="strategies-grid">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="strategy-card">
          <Skeleton width="35%" className="mb-2" />
          <Skeleton width="85%" className="mb-1" />
          <Skeleton width="78%" className="mb-1" />
          <Skeleton width="62%" />
        </div>
      ))}
    </div>
  </div>
));
StrategiesSkeleton.displayName = "StrategiesSkeleton";

const ResourcesSkeleton = React.memo(() => (
  <div className="plan-section resources-section">
    <Skeleton width="100%" height="2rem" className="mb-2" />
    <div className="resources-grid">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="resource-category">
          <Skeleton width="30%" className="mb-2" />
          <Skeleton width="85%" className="mb-1" />
          <Skeleton width="72%" className="mb-1" />
          <Skeleton width="64%" />
        </div>
      ))}
    </div>
  </div>
));
ResourcesSkeleton.displayName = "ResourcesSkeleton";

const TimelineSkeleton = React.memo(() => (
  <div className="plan-section resources-section">
    <Skeleton width="100%" height="2rem" className="mb-2" />
    <div className="ml-4">
      <Skeleton width="45%" className="mb-2" />
      <Skeleton width="70%" className="mb-3" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="mb-3">
          <Skeleton width="30%" className="mb-2" />
          <Skeleton width="80%" className="mb-1" />
          <Skeleton width="72%" />
        </div>
      ))}
    </div>
  </div>
));
TimelineSkeleton.displayName = "TimelineSkeleton";

const PlanTemplate = ({
  content,
  languageText,
  saveItems,
  features_id = null,
  is_saved = false,
  isLoading = false,
  isStreaming = false,
}) => {
  const [isSaved, setIsSaved] = useState(is_saved || false);

  useEffect(() => {
    setIsSaved(is_saved || false);
  }, [is_saved]);

  const currentPlanData = useMemo(() => {
    if (!content) return {};
    try {
      if (typeof content === "string") return JSON.parse(content);
      if (content?.learning_plan) return content.learning_plan;
      if (content?.title) return content;
      return content;
    } catch {
      return content?.learning_plan || content || {};
    }
  }, [content]);

  const skillsLabel = useMemo(
    () => languageText?.skills || {},
    [languageText?.skills]
  );
  const resTypeLabel = useMemo(
    () => languageText?.recommendedResources || {},
    [languageText?.recommendedResources]
  );

  // Memoize computed values
  const hasWeeklyPlan = useMemo(
    () =>
      currentPlanData?.weekly_plan && currentPlanData.weekly_plan.length > 0,
    [currentPlanData?.weekly_plan]
  );

  const hasLearningStrategies = useMemo(
    () =>
      currentPlanData?.learning_strategies &&
      Object.keys(currentPlanData.learning_strategies).length > 0,
    [currentPlanData?.learning_strategies]
  );

  const hasRecommendedResources = useMemo(
    () =>
      currentPlanData?.recommended_resources &&
      Object.keys(currentPlanData.recommended_resources).length > 0,
    [currentPlanData?.recommended_resources]
  );

  const hasTimeline = useMemo(
    () => !!currentPlanData?.timeline,
    [currentPlanData?.timeline]
  );

  const learningStrategiesEntries = useMemo(
    () =>
      hasLearningStrategies
        ? Object.entries(currentPlanData.learning_strategies)
        : [],
    [hasLearningStrategies, currentPlanData?.learning_strategies]
  );

  const recommendedResourcesEntries = useMemo(
    () =>
      hasRecommendedResources
        ? Object.entries(currentPlanData.recommended_resources)
        : [],
    [hasRecommendedResources, currentPlanData?.recommended_resources]
  );

  // Memoize save handler
  const handleSave = useCallback(() => {
    saveItems({
      features_id: features_id,
      session_type: LEARNING_PLAN,
    });
    setIsSaved((v) => !v);
  }, [saveItems, features_id]);

  // Memoize section renders
  const weeklyPlanSection = useMemo(() => {
    if (!hasWeeklyPlan) return null;
    return (
      <div className="plan-section daily-plan-section">
        <span className="section-title flex align-items-center gap-2">
          <i className="pi pi-calendar text-xl mb-1"></i>
          <span className="white-space-nowrap">
            {languageText?.dailyStudyPlan || "Daily Study Plan"}
          </span>
          <Divider />
        </span>
        <div className="daily-plan-grid grid">
          {currentPlanData.weekly_plan.map((week, index) => (
            <div key={index} className="day-card">
              <div className="day-header">
                <h4 className="day-name">{week?.name}</h4>
                <span className="day-time">({week?.time})</span>
              </div>
              <p className="day-focus">{week?.focus}</p>
              <ul className="day-activities">
                {week?.activity?.map((activity, actIndex) => (
                  <li key={actIndex} className="activity-item">
                    {activity?.item || activity}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  }, [
    hasWeeklyPlan,
    currentPlanData.weekly_plan,
    languageText?.dailyStudyPlan,
  ]);

  const strategiesSection = useMemo(() => {
    if (!hasLearningStrategies) return null;
    return (
      <div className="plan-section strategies-section">
        <h3 className="section-title flex align-items-center gap-2">
          <i className="pi pi-bullseye text-xl"></i>
          <span className="white-space-nowrap">
            {languageText?.specificLearningStrategies ||
              "Specific Learning Strategies"}
          </span>
          <Divider />
        </h3>
        <div className="strategies-grid">
          {learningStrategiesEntries.map(([area, strategy]) => (
            <div key={area} className="strategy-card">
              <h4 className="strategy-area">{skillsLabel?.[area] || area}</h4>
              <ul className="strategy-list ml-2">
                {(strategy?.item || []).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  }, [
    hasLearningStrategies,
    learningStrategiesEntries,
    skillsLabel,
    languageText?.specificLearningStrategies,
  ]);

  const resourcesSection = useMemo(() => {
    if (!hasRecommendedResources) return null;
    return (
      <div className="plan-section resources-section">
        <span className="section-title flex align-items-center gap-2">
          <i className="pi pi-book text-xl"></i>
          <span className="white-space-nowrap">
            {languageText?.recommendedStudyMaterials ||
              "Recommended Study Materials"}
          </span>
          <Divider />
        </span>
        <div className="resources-grid grid">
          {recommendedResourcesEntries.map(([type, resources]) => (
            <div key={type} className="resource-category">
              <h4 className="resource-type">{resTypeLabel?.[type] || type}</h4>
              <ul className="resource-list ml-2">
                {(resources?.item || []).map((resource, index) => (
                  <li key={index} className="resource-item">
                    {resource}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  }, [
    hasRecommendedResources,
    recommendedResourcesEntries,
    resTypeLabel,
    languageText?.recommendedStudyMaterials,
  ]);

  const timelineSection = useMemo(() => {
    if (!hasTimeline) return null;
    return (
      <div className="plan-section resources-section">
        <span className="section-title flex align-items-center gap-2">
          <i className="pi pi-list text-xl"></i>
          <span className="white-space-nowrap">
            {languageText?.timeline || "Timeline"}
          </span>
          <Divider />
        </span>
        <div className="flex flex-column ml-4">
          {currentPlanData.timeline?.subject && (
            <span className="text-base mb-2">
              {currentPlanData.timeline.subject}
            </span>
          )}
          {currentPlanData.timeline?.summary && (
            <span className="text-base mb-2">
              {currentPlanData.timeline.summary}
            </span>
          )}
          {currentPlanData.timeline?.timeline?.map((timeline, index) => (
            <span key={timeline?.id || index} className="ml-2">
              <span className="font-bold"> {timeline?.name} : </span>
              <ul className="ml-2 my-3">
                {timeline?.objective?.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </span>
          ))}
        </div>
      </div>
    );
  }, [hasTimeline, currentPlanData.timeline, languageText?.timeline]);

  return (
    <div className="learning-plan-template">
      {/* Header */}
      {isLoading && !currentPlanData?.title && <HeaderSkeleton />}
      {currentPlanData?.title && (
        <div className="plan-header">
          <div className="plan-title-section">
            <h2 className="plan-title">{currentPlanData.title}</h2>
            {currentPlanData.overview && (
              <p className="plan-overview">{currentPlanData.overview}</p>
            )}
          </div>
        </div>
      )}

      {/* Daily Study Plan (from weekly_plan) */}
      {isLoading && !currentPlanData?.weekly_plan && <DailySkeleton />}
      {weeklyPlanSection}

      {/* Study Strategies by Area */}
      {isLoading && !currentPlanData?.learning_strategies && (
        <StrategiesSkeleton />
      )}
      {strategiesSection}

      {/* Recommended Learning Materials */}
      {isLoading && !currentPlanData?.recommended_resources && (
        <ResourcesSkeleton />
      )}
      {resourcesSection}

      {/* Timeline Learning Materials */}
      {isLoading && !currentPlanData?.timeline && <TimelineSkeleton />}
      {timelineSection}

      {/* Footer button */}
      {isLoading || isStreaming ? (
        <div className="flex justify-content-end mt-3">
          <Skeleton width="10rem" height="2.5rem" />
        </div>
      ) : (
        <div className="flex justify-content-end mt-3">
          <Button
            label={isSaved ? languageText?.unsaveBtn : languageText?.saveBtn}
            className={`save-library-button ${isSaved ? "saved-button" : ""}`}
            onClick={handleSave}
          />
        </div>
      )}
    </div>
  );
};

// vocabularyTemplate.jsx
const langLabel = (code) => {
  if (!code) return "";
  const c = code.toLowerCase();
  if (c === "en") return "EN";
  if (c === "ko") return "KO";
  if (c === "vi" || c === "vn") return "VI";
  return code.toUpperCase();
};

const VocabularyTemplate = ({
  content,
  languageText,
  saveItems,
  features_id = null,
  is_saved = false,
}) => {
  let data = typeof content === "string" ? JSON.parse(content) : content;
  const [isSaved, setIsSaved] = useState(is_saved || false);
  const {
    word,
    pronunciation,
    level,
    type,
    definitions = [],
    synonyms = [],
    antonyms = [],
    examples = [],
    etymology,
    related = [],
  } = data;

  return (
    <div className="vocab-wrap">
      {/* Top bar */}
      <div className="vocab-topbar">
        <div className="v-top-left">
          <span className="keduall-ai-tag">
            {level || "Intermediate"}
          </span>
          <span className="keduall-ai-tag">
            {type || "Verb"}
          </span>
        </div>
      </div>

      {/* Title row */}
      <div className="v-title-row">
        <span className="v-hint">{languageText.hint}</span>
        <div className="flex align-items-center gap-2">
          <h1 className="v-title">{word}</h1>
          {pronunciation && <span className="v-roman">{pronunciation}</span>}
        </div>
      </div>

      {/* Section: 의미 */}
      <div className="v-section">
        <h2 className="v-sec-title">{languageText.vocabularyTitle}</h2>
        <div className="v-def-list">
          {definitions.map((d, i) => (
            <div key={i} className="v-def">
              <div className="v-def-lang">{langLabel(d.lang)}</div>
              <div className="v-def-text">{d.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 유의어 / 반의어 */}
      {(synonyms.length > 0 || antonyms.length > 0) && (
        <div className="v-two-col">
          {synonyms.length > 0 && (
            <div className="v-col">
              <h3 className="v-sub">{languageText.synonyms}</h3>
              <div className="v-chip-row">
                {synonyms.map((s, i) => (
                  <span key={i} className="v-chip outline">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {antonyms.length > 0 && (
            <div className="v-col">
              <h3 className="v-sub">{languageText.antonyms}</h3>
              <div className="v-chip-row">
                {antonyms.map((a, i) => (
                  <span key={i} className="v-chip outline">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 예문 */}
      {examples.length > 0 && (
        <div className="v-section">
          <h2 className="v-sec-title">{languageText.examples}</h2>
          <div className="v-ex-list">
            {examples.map((ex, i) => (
              <div key={i} className="v-ex">
                <div className="v-ex-label">{`Ex.${i + 1}`}</div>
                <div className="v-ex-body">
                  {ex.ko && <p className="v-ex-ko">{ex.ko}</p>}
                  {ex.translation && (
                    <p className="v-ex-tr">
                      {ex.translation}
                      {ex.translationLang
                        ? ` (${langLabel(ex.translationLang)})`
                        : ""}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 어원 */}
      {etymology && (
        <div className="v-section">
          <h2 className="v-sec-title">{languageText.etymology}</h2>
          <p className="v-para">{etymology}</p>
        </div>
      )}

      {/* 관련 */}
      {related.length > 0 && (
        <div className="v-section">
          <h2 className="v-sec-title">{languageText.related}</h2>
          <div className="v-chip-row">
            {related.map((r, i) => (
              <span key={i} className="keduall-ai-tag">
                {r}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-content-end">
        <button
          className={`save-library-button ${isSaved ? "saved-button" : ""}`}
          onClick={() => {
            saveItems({
              features_id: features_id,
              session_type: VOCAB_EXPANSION,
            });
            setIsSaved(!isSaved);
          }}
        >
          {isSaved ? languageText.unsaveBtn : languageText.saveBtn}
        </button>
      </div>
    </div>
  );
};

const TopikTemplate = ({
  content,
  languageText,
  saveItems,
  features_id = null,
  is_saved = false,
}) => {
  const [isSaved, setIsSaved] = useState(is_saved || false);

  let topikData;
  if (typeof content === "string") {
    try {
      topikData = JSON.parse(content);
    } catch {
      return textTemplate({ content });
    }
  } else {
    topikData = content;
  }

  const meta = topikData?.metadata || {};
  const levelText = meta?.level ? languageText?.levels?.[meta.level.toLowerCase()] : "";
  const metaType = meta?.type ? meta.type.toLowerCase() : "";
  const typeText =
    (metaType && languageText?.types?.[metaType]) || meta?.type || "";
  const subtypeKey = meta?.subtype
    ? meta.subtype.replace(/_(\w)/g, (_, char) => char.toUpperCase())
    : "";
  const subtypeText =
    meta?.type && languageText?.[meta.type]?.[subtypeKey]
      ? languageText[meta.type][subtypeKey]
      : meta?.subtype || "";

  return (
    <div className="topik-template">
      <div className="topik-question-header">
        <div className="topik-title-section">
          {levelText && <span className="text-900 text-sm">{levelText}</span>}
          {typeText && <span className="keduall-ai-tag">{typeText}</span>}
          {subtypeText && <span className="keduall-ai-tag">{subtypeText}</span>}
          {meta?.topic && <span className="keduall-ai-tag">{meta.topic}</span>}
        </div>
      </div>

      <TopikInteractiveCard data={topikData} languageText={languageText} />

      <div className="flex justify-content-end mt-5">
        <Button
          label={isSaved ? languageText.unsaveBtn : languageText.saveBtn}
          className={`save-library-button ${isSaved ? "saved-button" : ""}`}
          onClick={() => {
            saveItems({
              features_id: features_id,
              session_type: TOPIK_GENERATION,
              session_id: topikData?.session_id,
            });
            setIsSaved((prev) => !prev);
          }}
        />
      </div>
    </div>
  );
};

const MemoizedPlanTemplate = React.memo(PlanTemplate);

export const MessageTemplates = {
  text: textTemplate,
  plan: ({
    content,
    languageText,
    saveItems,
    features_id = null,
    is_saved = false,
    isLoading = false,
    isStreaming = false,
  }) => (
    <MemoizedPlanTemplate
      content={content}
      languageText={languageText}
      saveItems={saveItems}
      features_id={features_id}
      is_saved={is_saved}
      isLoading={isLoading}
      isStreaming={isStreaming}
    />
  ),
  vocabulary: ({
    content,
    languageText,
    saveItems,
    features_id = null,
    is_saved = false,
  }) => {
    return (
      <VocabularyTemplate
        content={content}
        languageText={languageText}
        saveItems={saveItems}
        features_id={features_id}
        is_saved={is_saved}
      />
    );
  },
  topik: ({
    content,
    languageText,
    saveItems,
    features_id = null,
    is_saved = false,
  }) => (
    <TopikTemplate
      content={content}
      languageText={languageText}
      saveItems={saveItems}
      features_id={features_id}
      is_saved={is_saved}
    />
  ),
};
